import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { AlertCircle, Loader2, Plus, Sparkles, X } from 'lucide-react'
import { z } from 'zod'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  demodaySignupAppDemodaySignupPostMutation,
  joinAppDemodayJoinPostMutation,
} from '@/client/@tanstack/react-query.gen'
import { DemodayWelcomeOverlay } from '@/components/demoday-welcome-overlay'
import { useAuth } from '@/hooks/use-auth'
import { DEMODAY_WELCOME_KEY, SESSION_FLAG_KEY } from '@/lib/constants'
import { LoadingTimeline } from './-components/loading-timeline'
import { OrgPicker } from './-components/org-picker'
import type { SelectedOrg } from './-components/org-picker'
import { WelcomeScreen } from './-components/welcome-screen'

const MAX_TEAM_MEMBERS = 4

interface DemodaySearch {
  preview?: PreviewStage
}

type PreviewStage = 'loading' | 'loading-completing' | 'welcome' | 'overlay'

const PREVIEW_STAGES: ReadonlySet<PreviewStage> = new Set([
  'loading',
  'loading-completing',
  'welcome',
  'overlay',
])

export const Route = createFileRoute('/demoday/')({
  validateSearch: (search: Record<string, unknown>): DemodaySearch => {
    const preview = search['preview'] // eslint-disable-line dot-notation
    if (typeof preview === 'string' && (PREVIEW_STAGES as ReadonlySet<string>).has(preview)) {
      return { preview: preview as PreviewStage }
    }
    return {}
  },
  component: DemoDaySignupPage,
})

const PREVIEW_FIRST_NAME = 'Preview'

// Discriminated union for the top-level page stage
type FormStage = 'picker' | 'form' | 'fallback'

function previewToStage(preview: PreviewStage | undefined): Stage {
  switch (preview) {
    case 'loading': {
      return { kind: 'loading', isCompleting: false }
    }
    case 'loading-completing': {
      return { kind: 'loading', isCompleting: true }
    }
    case 'welcome': {
      return {
        kind: 'welcome',
        firstName: PREVIEW_FIRST_NAME,
        organizationName: 'SUPPLYCO Demo',
        emailsSent: 1,
      }
    }
    case 'overlay': {
      return { kind: 'overlay-preview', firstName: PREVIEW_FIRST_NAME }
    }
    default: {
      return { kind: 'form', formStage: 'picker', selectedOrg: undefined }
    }
  }
}

type Stage
  = | { kind: 'form', formStage: FormStage, selectedOrg: SelectedOrg | undefined }
    | { kind: 'loading', isCompleting: boolean }
    | {
      kind: 'welcome'
      firstName: string
      organizationName: string
      emailsSent: number
    }
    | { kind: 'overlay-preview', firstName: string }
    | { kind: 'error', message: string, formStage: FormStage, selectedOrg: SelectedOrg | undefined }

interface TeamMemberDraft {
  /**
   * Stable client-side id so we can re-render rows on add/remove without
   * collapsing onto the email field (the natural key is empty for new rows).
   */
  id: string
  firstName: string
  lastName: string
  email: string
}

const memberSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(80),
  lastName: z.string().min(1, 'Last name is required').max(80),
  email: z.string().email('Enter a valid email'),
})

const fallbackFormSchema = z.object({
  primary: memberSchema,
  teamMembers: z.array(memberSchema).max(MAX_TEAM_MEMBERS),
  website: z
    .string()
    .min(3, 'Website is required')
    .refine(value => value.includes('.'), {
      message: 'That doesn\'t look like a website (e.g. acme.com)',
    }),
})

const joinFormSchema = z.object({
  primary: memberSchema,
  teamMembers: z.array(memberSchema).max(MAX_TEAM_MEMBERS),
})

function newDraftMember(): TeamMemberDraft {
  return {
    id: globalThis.crypto.randomUUID(),
    firstName: '',
    lastName: '',
    email: '',
  }
}

interface SuccessPayload {
  firstName: string
  organizationName: string
  emailsSent: number
}

function DemoDaySignupPage() {
  const { preview } = Route.useSearch()
  const [stage, setStage] = useState<Stage>(() => previewToStage(preview))
  const navigate = useNavigate()
  const { markSessionEstablished } = useAuth()

  // Form state — kept as plain React state rather than TanStack Form so the
  // dynamic teamMembers array stays trivial to add/remove from. Pre-submit
  // validation happens via the zod schema in handleSubmit.
  const [primary, setPrimary] = useState({ firstName: '', lastName: '', email: '' })
  const [teamMembers, setTeamMembers] = useState<TeamMemberDraft[]>([])
  const [website, setWebsite] = useState('')
  const [validationError, setValidationError] = useState<string | undefined>()
  // Id of the most-recently-added teammate row. The matching TeammateRow
  // reads this on mount via the `autoFocusOnMount` prop, scrolls itself
  // into view, and focuses its first-name input. Stays set indefinitely
  // — that's fine because newDraftMember() generates a fresh UUID every
  // time, so it only ever matches the one row we want to focus.
  const [newlyAddedMemberId, setNewlyAddedMemberId] = useState<string | undefined>()

  // Hold the SDK response until the loading timeline finishes its animation,
  // so the welcome screen doesn't flash before the user sees the final step.
  const pendingResponse = useRef<SuccessPayload | null>(null)

  const signupMutation = useMutation(demodaySignupAppDemodaySignupPostMutation())
  const joinMutation = useMutation(joinAppDemodayJoinPostMutation())

  // Derive current formStage / selectedOrg from the stage discriminant
  const currentFormStage: FormStage
    = stage.kind === 'form' || stage.kind === 'error'
      ? stage.formStage
      : 'picker'
  const currentSelectedOrg: SelectedOrg | undefined
    = stage.kind === 'form' || stage.kind === 'error'
      ? stage.selectedOrg
      : undefined

  const handleSubmitSuccess = (payload: SuccessPayload) => {
    // Safety belt for refreshes between submit and the user clicking
    // Continue: AuthProvider reads this flag at mount, so without it a
    // refresh on the welcome screen would orphan the user from their
    // freshly-issued cookie. handleContinue does the real auth-state
    // sync via markSessionEstablished() before navigating.
    localStorage.setItem(SESSION_FLAG_KEY, 'true')
    pendingResponse.current = payload
    setStage(current =>
      current.kind === 'loading'
        ? { kind: 'loading', isCompleting: true }
        : current,
    )
  }

  const handleContinue = async (firstName: string) => {
    // Drop a one-shot handoff flag and navigate to the dashboard. The
    // dashboard reads this flag on mount and renders the brand-takeover
    // overlay *over* its first paint — so the animation feels like the
    // destination revealing itself, not a transition on /demoday.
    sessionStorage.setItem(
      DEMODAY_WELCOME_KEY,
      JSON.stringify({ firstName, ts: Date.now() }),
    )
    // Full auth-state sync (localStorage flag + React `hasSession` +
    // refetch). Without setting `hasSession`, a later logout would call
    // setHasSession(false) as a no-op and the router.navigate('/login')
    // would run against stale context — bouncing the user back to the
    // dashboard with no user object loaded.
    await markSessionEstablished()
    await navigate({ to: '/' })
  }

  const handlePreviewOverlayDone = () => {
    setStage({
      kind: 'welcome',
      firstName: PREVIEW_FIRST_NAME,
      organizationName: 'SUPPLYCO Demo',
      emailsSent: 1,
    })
  }

  const handleAnimationComplete = () => {
    const response = pendingResponse.current
    if (response) {
      setStage({
        kind: 'welcome',
        firstName: response.firstName,
        organizationName: response.organizationName,
        emailsSent: response.emailsSent,
      })
    }
  }

  const addTeamMember = () => {
    if (teamMembers.length >= MAX_TEAM_MEMBERS) {
      return
    }
    const draft = newDraftMember()
    setTeamMembers(prev => [...prev, draft])
    setNewlyAddedMemberId(draft.id)
  }

  const removeTeamMember = (id: string) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id))
  }

  const updateTeamMember = (id: string, patch: Partial<TeamMemberDraft>) => {
    setTeamMembers(prev => prev.map(m => (m.id === id ? { ...m, ...patch } : m)))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setValidationError(undefined)

    const trimmedPrimary = {
      firstName: primary.firstName.trim(),
      lastName: primary.lastName.trim(),
      email: primary.email.trim(),
    }
    const trimmedTeamMembers = teamMembers.map(m => ({
      firstName: m.firstName.trim(),
      lastName: m.lastName.trim(),
      email: m.email.trim(),
    }))

    // Client-side dedupe — the API enforces this too, but failing early
    // saves a 25 s Gemini round-trip if the user typo'd a teammate's
    // email to match the primary.
    const seen = new Set<string>()
    for (const { email } of [trimmedPrimary, ...trimmedTeamMembers]) {
      const lower = email.toLowerCase()
      if (seen.has(lower)) {
        setValidationError(`The email "${email}" is repeated in your team list. Each user needs a unique email.`)
        return
      }
      seen.add(lower)
    }

    if (currentFormStage === 'form' && currentSelectedOrg) {
      // Join path — org already selected; no website field
      const payload = {
        primary: trimmedPrimary,
        teamMembers: trimmedTeamMembers,
      }
      const result = joinFormSchema.safeParse(payload)
      if (!result.success) {
        setValidationError(result.error.issues.map(i => i.message).join(' • '))
        return
      }
      setStage({ kind: 'loading', isCompleting: false })
      joinMutation.mutate(
        {
          body: {
            organizationId: currentSelectedOrg.id,
            primary: trimmedPrimary,
            teamMembers: trimmedTeamMembers,
          },
        },
        {
          onSuccess: (data) => {
            handleSubmitSuccess({
              firstName: data.firstName,
              organizationName: data.organizationName,
              emailsSent: data.emailsSent,
            })
          },
          onError: (error) => {
            const message = extractErrorMessage(error)
            setStage({ kind: 'error', message, formStage: 'form', selectedOrg: currentSelectedOrg })
          },
        },
      )
    }
    else {
      // Fallback (Gemini) path — website field required
      const payload = {
        website: website.trim(),
        primary: trimmedPrimary,
        teamMembers: trimmedTeamMembers,
      }
      const result = fallbackFormSchema.safeParse(payload)
      if (!result.success) {
        setValidationError(result.error.issues.map(i => i.message).join(' • '))
        return
      }
      setStage({ kind: 'loading', isCompleting: false })
      signupMutation.mutate(
        { body: payload },
        {
          onSuccess: (data) => {
            handleSubmitSuccess({
              firstName: data.firstName,
              organizationName: data.organizationName,
              emailsSent: data.emailsSent,
            })
          },
          onError: (error) => {
            const message = extractErrorMessage(error)
            setStage({ kind: 'error', message, formStage: 'fallback', selectedOrg: undefined })
          },
        },
      )
    }
  }

  if (stage.kind === 'overlay-preview') {
    return (
      <DemodayWelcomeOverlay
        firstName={stage.firstName}
        onComplete={handlePreviewOverlayDone}
      />
    )
  }

  if (stage.kind === 'welcome') {
    return (
      <WelcomeScreen
        firstName={stage.firstName}
        organizationName={stage.organizationName}
        emailsSent={stage.emailsSent}
        onContinue={() => {
          void handleContinue(stage.firstName)
        }}
      />
    )
  }

  if (stage.kind === 'loading') {
    return (
      <LoadingTimeline
        isCompleting={stage.isCompleting}
        onAnimationComplete={handleAnimationComplete}
      />
    )
  }

  const isPending = joinMutation.isPending || signupMutation.isPending
  const isError = stage.kind === 'error'

  // Picker stage
  if (currentFormStage === 'picker') {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white/80 p-8 shadow-lg backdrop-blur">
        <div className="mb-6 flex items-center gap-2">
          <span className="rounded-full bg-blue-100 p-2">
            <Sparkles className="h-4 w-4 text-blue-700" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">
            Demo Day Signup
          </span>
        </div>

        <div className="mb-8 space-y-2">
          <h1 className="text-3xl font-bold text-slate-900 md:text-4xl">
            Find your company
          </h1>
          <p className="text-base text-muted-foreground md:text-lg">
            Select your company from the list below to join your team's SUPPLYCO workspace.
          </p>
        </div>

        <OrgPicker
          onConfirm={(org) => {
            setStage({ kind: 'form', formStage: 'form', selectedOrg: org })
          }}
          onNotListed={() => {
            setStage({ kind: 'form', formStage: 'fallback', selectedOrg: undefined })
          }}
        />
      </div>
    )
  }

  // Form and fallback stages share the same user fields; only the heading,
  // website field visibility, and back button differ.
  return (
    <div className="rounded-2xl border border-slate-200 bg-white/80 p-8 shadow-lg backdrop-blur">
      <div className="mb-6 flex items-center gap-2">
        <span className="rounded-full bg-blue-100 p-2">
          <Sparkles className="h-4 w-4 text-blue-700" />
        </span>
        <span className="text-xs font-semibold uppercase tracking-wider text-blue-700">
          Demo Day Signup
        </span>
      </div>

      <div className="mb-8 space-y-2">
        {currentFormStage === 'form' && currentSelectedOrg
          ? (
              <h1 className="text-3xl font-bold text-slate-900 md:text-4xl">
                Join
                {' '}
                {currentSelectedOrg.name}
              </h1>
            )
          : (
              <h1 className="text-3xl font-bold text-slate-900 md:text-4xl">
                Spin up your SUPPLYCO workspace
              </h1>
            )}
        <p className="text-base text-muted-foreground md:text-lg">
          {currentFormStage === 'form'
            ? 'Enter your details and we\'ll set up your account.'
            : 'Enter your details and your company website. We\'ll research your company in real time and have you logged in in under a minute.'}
        </p>
      </div>

      {isError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{stage.message}</AlertDescription>
        </Alert>
      )}

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Check your inputs</AlertTitle>
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FieldGroup>
          <div>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">
              You (account admin)
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="primary-first-name">First name</FieldLabel>
                <Input
                  id="primary-first-name"
                  autoComplete="given-name"
                  value={primary.firstName}
                  onChange={e => setPrimary(p => ({ ...p, firstName: e.target.value }))}
                  placeholder="Jane"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="primary-last-name">Last name</FieldLabel>
                <Input
                  id="primary-last-name"
                  autoComplete="family-name"
                  value={primary.lastName}
                  onChange={e => setPrimary(p => ({ ...p, lastName: e.target.value }))}
                  placeholder="Smith"
                />
              </Field>
            </div>
            <Field className="mt-4">
              <FieldLabel htmlFor="primary-email">Work email</FieldLabel>
              <Input
                id="primary-email"
                type="email"
                autoComplete="email"
                value={primary.email}
                onChange={e => setPrimary(p => ({ ...p, email: e.target.value }))}
                placeholder="jane@acme.com"
              />
            </Field>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Teammates
                {' '}
                <span className="text-slate-400 normal-case font-normal">
                  (optional — we'll create accounts for each one)
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={teamMembers.length >= MAX_TEAM_MEMBERS}
                onClick={addTeamMember}
              >
                <Plus className="mr-1 h-3 w-3" />
                Add teammate
              </Button>
            </div>

            {teamMembers.length === 0
              ? (
                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-3 text-sm text-muted-foreground">
                    Just you for now? No problem — add teammates above to spin
                    up accounts for them too.
                  </p>
                )
              : (
                  <div className="space-y-2">
                    {teamMembers.map(member => (
                      <TeammateRow
                        key={member.id}
                        member={member}
                        autoFocusOnMount={member.id === newlyAddedMemberId}
                        onChange={patch => updateTeamMember(member.id, patch)}
                        onRemove={() => removeTeamMember(member.id)}
                      />
                    ))}
                  </div>
                )}
          </div>

          {currentFormStage === 'fallback' && (
            <Field>
              <FieldLabel htmlFor="website">Company website</FieldLabel>
              <Input
                id="website"
                type="text"
                inputMode="url"
                autoComplete="url"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="acme.com"
              />
              <FieldDescription>
                We'll use this to research your company and prefill your workspace.
              </FieldDescription>
              <FieldError />
            </Field>
          )}
        </FieldGroup>

        {currentFormStage === 'form' && (
          <button
            type="button"
            onClick={() => {
              setStage({ kind: 'form', formStage: 'picker', selectedOrg: undefined })
            }}
            className="mt-4 text-sm text-muted-foreground underline"
          >
            ← Different company
          </button>
        )}

        <Button
          type="submit"
          size="lg"
          className="mt-6 w-full"
          disabled={isPending}
        >
          {isPending
            ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Working…
                </>
              )
            : (
                'Spin up my SUPPLYCO →'
              )}
        </Button>
      </form>
    </div>
  )
}

interface TeammateRowProps {
  member: TeamMemberDraft
  /** Set on the row that was just added so we can scroll-into-view + focus. */
  autoFocusOnMount: boolean
  onChange: (patch: Partial<TeamMemberDraft>) => void
  onRemove: () => void
}

/**
 * Compact 3-input row for one teammate. Drops the per-row "Teammate N"
 * header and the standalone field labels — placeholders are enough at
 * this scale — so a stack of four rows still fits comfortably in the
 * /demoday card without forcing the user to scroll a long form just to
 * find the submit button.
 *
 * When mounted with `autoFocusOnMount=true` (i.e. just-added row), the
 * row scrolls itself into view and focuses the first-name input so the
 * user can start typing without hunting for the new field.
 */
function TeammateRow({ member, autoFocusOnMount, onChange, onRemove }: TeammateRowProps) {
  const firstNameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!autoFocusOnMount || !firstNameRef.current) {
      return
    }
    firstNameRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
    // Defer the focus a tick so the smooth-scroll doesn't get hijacked by
    // the browser auto-centering on the focused element with `auto` behavior.
    const t = setTimeout(() => firstNameRef.current?.focus(), 250)
    return () => clearTimeout(t)
  }, [autoFocusOnMount])

  return (
    <div className="relative rounded-lg border border-slate-200 bg-slate-50/40 p-3">
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-2 top-2 rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700"
        aria-label="Remove teammate"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="grid gap-2 pr-8 md:grid-cols-2">
        <Input
          ref={firstNameRef}
          value={member.firstName}
          onChange={e => onChange({ firstName: e.target.value })}
          placeholder="First name"
          aria-label="Teammate first name"
        />
        <Input
          value={member.lastName}
          onChange={e => onChange({ lastName: e.target.value })}
          placeholder="Last name"
          aria-label="Teammate last name"
        />
      </div>
      <Input
        type="email"
        className="mt-2"
        value={member.email}
        onChange={e => onChange({ email: e.target.value })}
        placeholder="Work email"
        aria-label="Teammate email"
      />
    </div>
  )
}

function extractErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'detail' in error) {
    const detail = (error as { detail?: unknown }).detail
    if (detail === 'EMAIL_ALREADY_REGISTERED') {
      return 'One of those emails is already on a SUPPLYCO account. Try logging in instead.'
    }
    if (detail === 'DUPLICATE_EMAIL_IN_REQUEST') {
      return 'You\'ve listed the same email twice — each user needs a unique email.'
    }
    if (typeof detail === 'string') {
      return detail
    }
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'We hit an unexpected error. Please try again.'
}
